import React, { useEffect, useRef, useState } from "react";
import { css, styled } from "styled-components";
import Button from "./Button";
import { X } from "react-feather";

interface Props {
  children: React.ReactNode;
  buttons?: React.ReactNode;
  title?: React.ReactNode;
  footer?: React.ReactNode;
  subTitle?: React.ReactNode;
  color?: string;
  pad?: boolean;
  modal?: boolean;
  onClose?: () => void;
  width?: string;
  className?: string;
  noMargin?: boolean;
}

const StyledSection = styled.div<{ $modal?: boolean; $noMargin?: boolean }>`
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.45);
  height: fit-content;
  margin-bottom: ${(props) => (props.$noMargin ? "0" : "40px")};
  background-color: var(--dark100);
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  overflow: hidden;

  ${(props) =>
    props.$modal &&
    css`
      position: relative;
      max-width: 90vw;
      max-height: 90vh;
      z-index: 50000;
      border-radius: 15px;
      border: 1px solid var(--dark500);
    `};
`;

const StyledTitle = styled.div<{ $modal?: boolean }>`
  background-color: var(--dark300);
  border-bottom: 1px solid var(--dark060);
  min-height: 45px;
  padding: 5px 10px 3px;
  font-weight: 500;
  font-size: 20px;
  color: var(--gray400);
  display: flex;
  align-items: center;
  flex-shrink: 0;
  border-top-right-radius: 2px;
  border-top-left-radius: 2px;

  > span {
    display: flex;
    align-items: center;

    > * + * {
      margin-left: 10px;
    }
  }

  ${(props) =>
    props.$modal &&
    css`
      padding: 0 15px;
      height: 50px;
    `};
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 5px;
  padding-left: 10px;
  row-gap: 2px;
  justify-content: flex-end;
  flex: 1 1 auto;
`;

const Padding = styled.div<{
  $doPad?: boolean;
  $padBottom?: boolean;
  $modal?: boolean;
}>`
  padding: ${(props) => (props.$doPad ? "20px 10px" : "0")};
  padding-bottom: ${(props) => (props.$padBottom ? "20px" : "0")};
  display: flex;
  min-height: 0; /* allow scrolls to fill height */
  flex-grow: 1;
  flex-direction: column;

  ${(props) =>
    props.$modal &&
    css`
      overflow: auto;
    `};
`;

const Footer = styled.div`
  background-color: var(--dark200);
  padding: 5px 10px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border-top: 1px solid var(--dark060);

  > * + * {
    margin-left: 10px;
  }
`;

const ModalBackground = styled.div`
  background-color: var(--overlay);
  position: fixed;
  height: 100vh;
  width: 100vw;
  left: 0;
  top: 0;
  z-index: 50000;
  padding-top: 5vh;
  display: flex;
  justify-content: center;
`;

const Section = (props: Props) => {
  const {
    children,
    title,
    buttons,
    pad,
    footer,
    modal,
    onClose,
    width,
    subTitle,
    className,
    noMargin,
  } = props;
  const backgroundRef = useRef<HTMLDivElement>(null);
  const [mousedownTarget, setMousedownTarget] = useState<EventTarget>();

  useEffect(() => {
    const keydown = (event: KeyboardEvent) => {
      if (modal && event.key === "Escape") {
        event.preventDefault();
        onClose?.();
      }
    };

    window.addEventListener("keydown", keydown);

    return () => {
      window.removeEventListener("keydown", keydown);
    };
  });

  const section = (
    <StyledSection
      $modal={modal}
      style={
        modal
          ? { width: width ?? "700px", minWidth: width ?? "700px" }
          : undefined
      }
      className={className}
      $noMargin={noMargin}
    >
      {title !== undefined && (
        <StyledTitle $modal={modal}>
          <span>{title}</span>
          <ButtonWrapper>
            {buttons}
            {onClose && <Button icon={X} onClick={() => onClose()} />}
          </ButtonWrapper>
        </StyledTitle>
      )}
      {subTitle}
      <Padding $doPad={pad} $padBottom={!footer && pad} $modal={modal}>
        {children}
      </Padding>
      {footer && <Footer>{footer}</Footer>}
    </StyledSection>
  );

  return (
    <>
      {modal ? (
        <ModalBackground
          ref={backgroundRef}
          onMouseDown={(event) => {
            setMousedownTarget(event.target);
          }}
          onMouseUp={(event) => {
            if (
              event.target === backgroundRef.current &&
              event.target === mousedownTarget
            ) {
              onClose?.();
            }
          }}
        >
          {section}
        </ModalBackground>
      ) : (
        section
      )}
    </>
  );
};

export default Section;
